import { useCallback, useRef, useEffect } from 'react';
import { useThinkingTimer  } from '../helpers/thinkingHelper';
import { useResourceContext } from '../contexts/ResourceContext';
import { useChatContext } from '../contexts/AppDataContext';
import { useModelContext } from '../contexts/ModelContext';
import { quizResourceFromStreamEvent } from '../helpers/quizHelper';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { Stream } from '../types/stream';
import { upsertBlock } from '../helpers/contentBlocks';
import { useMessageOperationsContext } from '../contexts/MessageOperationsContext';
import { stripTransientStreamMarkup } from '../helpers/streamContent';

/**
 * A hook to process streaming responses from the API
 * @returns {Object} - Functions to handle streaming responses
 */

export const useStreamProcessor = () => {
  const { currentThinkingTime, start, stop, reset } = useThinkingTimer();
  const { setIsWritingResource, handleCreateResource } = useResourceContext();
  const { startStreaming, useReasoning, syncThinkingTime, currentChatId, isIncognito } = useChatContext();
  const { currentModel } = useModelContext();
  const auth = useAuth();
  const { increaseImageGenerationCount } = auth;
  const { 
    updateMessageContent, 
    updateMessageSearchItems, 
    updateMessageId, 
    updateMessageTextAttachments, 
    updateMessageImages, 
    updateMessageBranchId, 
    updateMessagePairId, 
    updateMessageModel,
    stopGeneration
  } = useMessageOperationsContext();
  
  const thinkingTimeRef = useRef<number>(0);
  const toast  = useToast();
  thinkingTimeRef.current = currentThinkingTime;
  
  // Sync thinking time with ChatContext
  useEffect(() => {
    syncThinkingTime(currentThinkingTime);
  }, [currentThinkingTime, syncThinkingTime]);

  const startThinking = useCallback(() => {
    if (useReasoning || !currentModel || currentModel.isReasoning) {
      start();
    }
  }, [start, useReasoning, currentModel]);

  // Throttled writer (≤ 60 fps)
  const bufferRef = useRef<Record<string, string>>({});
  const frameRef  = useRef<number | undefined>(undefined);

  const scheduleFlush = useCallback((id: string) => {
    if (frameRef.current !== undefined) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = undefined;
      Object.entries(bufferRef.current).forEach(([msgId, content]) => {
        updateMessageContent(msgId, content);
      });
      // Don't clear the buffer - let it accumulate until the stream ends
    });
  }, [updateMessageContent]);

  const appendContent = useCallback((id: string, chunk: string) => {
    bufferRef.current[id] = (bufferRef.current[id] || '') + chunk;
    scheduleFlush(id);
  }, [scheduleFlush]);


  const processStream = useCallback(async (
    stream: Stream, 
    aiMessageId: string,
    userMessageId: string,
    onIdUpdate?: (newAiId: string) => void,
    /** Saved content of an answer resumed after a question card; new output is appended. */
    initialContent: string = '',
  ) => {
    let aiMessageIdtoUse = aiMessageId;
    let userMessageIdtoUse = userMessageId;
    let accumulatedResponse = initialContent;
    // The buffer outlives streams; a resumed answer restarts from its saved content.
    if (initialContent) bufferRef.current[aiMessageId] = initialContent;
    let isSaved = false;
    let planningOpen = false;
    let provisionalPlanningStart: number | null = null;
    let provisionalPlanningLength = 0;

    const removeProvisionalPlanningContent = () => {
      if (provisionalPlanningStart === null || provisionalPlanningLength === 0) return;
      accumulatedResponse = (
        accumulatedResponse.slice(0, provisionalPlanningStart)
        + accumulatedResponse.slice(provisionalPlanningStart + provisionalPlanningLength)
      );
      bufferRef.current[aiMessageIdtoUse] = accumulatedResponse;
      scheduleFlush(aiMessageIdtoUse);
      provisionalPlanningStart = null;
      provisionalPlanningLength = 0;
    };

    startStreaming();
    startThinking()
    
    try {
      if (stream) {
        for await (const chunk of stream) {
          if (chunk.error) {
            // Guest and incognito turns live only in local state: fail the
            // submission so its pair is removed and the input restored,
            // instead of leaving an empty reply in the conversation.
            if (!accumulatedResponse && (isIncognito || !auth.user)) {
              throw new Error(chunk.error);
            }
            toast(chunk.error, 'error');
          } else if (chunk.planningStart) {
            if (!planningOpen) {
              planningOpen = true;
              accumulatedResponse += '<planning>';
              appendContent(aiMessageIdtoUse, '<planning>');
            }
          } else if (chunk.planningEnd) {
            if (planningOpen) {
              planningOpen = false;
              accumulatedResponse += '</planning>';
              appendContent(aiMessageIdtoUse, '</planning>');
            }
          } else if (chunk.planningContent) {
            if (provisionalPlanningStart === null) {
              provisionalPlanningStart = accumulatedResponse.length;
            }
            provisionalPlanningLength += chunk.planningContent.length;
            accumulatedResponse += chunk.planningContent;
            appendContent(aiMessageIdtoUse, chunk.planningContent);
          } else if (chunk.reasoningContent) {
            removeProvisionalPlanningContent();
            if (thinkingTimeRef.current === 0) start();

            for (const char of chunk.reasoningContent) {
              accumulatedResponse += char;
              appendContent(aiMessageIdtoUse, char);
            }
            
            if (chunk.reasoningContent.includes('</think>')) {
              isSaved = true;
              stop();
              reset();
            }
          } else if (chunk.content) {
            if (!planningOpen) {
              removeProvisionalPlanningContent();
              reset();
              stop();
              if (!isSaved) {
                isSaved = true;
                stop();
              }
            }
            
            // Process character by character with a small delay
            for (const char of chunk.content) {
              accumulatedResponse += char;
              appendContent(aiMessageIdtoUse, char);
            }
            
          } else if (chunk.reasoningSummary) {
            removeProvisionalPlanningContent();
            if (thinkingTimeRef.current === 0) start();
            for (const char of chunk.reasoningSummary) {
              accumulatedResponse += char;
              appendContent(aiMessageIdtoUse, char);
            }
          } else if (chunk.block) {
            accumulatedResponse = upsertBlock(accumulatedResponse, chunk.block);
            bufferRef.current[aiMessageIdtoUse] = accumulatedResponse;
            scheduleFlush(aiMessageIdtoUse);
            const { tag, xml } = chunk.block;
            if (tag === 'image_generation' && xml.includes('<status>done</status>')) {
              const count = Number(xml.match(/<number_of_images>(\d+)<\/number_of_images>/)?.[1]) || 1;
              increaseImageGenerationCount(count);
            }
          } else if (chunk.blocked) {
            toast('This response was blocked.', 'error');
          } else if (chunk.quiz) {
            const quizResource = quizResourceFromStreamEvent(chunk.quiz, currentChatId || "");
            if (quizResource) handleCreateResource(quizResource);
            toast('Quiz Generated', "success");
          } else if (chunk.search_items !== undefined) {
            updateMessageSearchItems(aiMessageIdtoUse, chunk.search_items);
          } else if (chunk.userMessageId !== undefined) {
            updateMessageId(userMessageIdtoUse, chunk.userMessageId);
            userMessageIdtoUse = chunk.userMessageId;
          } else if (chunk.aiMessageId !== undefined) {
            updateMessageId(aiMessageIdtoUse, chunk.aiMessageId);
            aiMessageIdtoUse = chunk.aiMessageId;
            if (onIdUpdate) {
              onIdUpdate(chunk.aiMessageId);
            }
          } else if (chunk.textAttachments !== undefined) {
            updateMessageTextAttachments(userMessageIdtoUse, chunk.textAttachments);
          } else if (chunk.images !== undefined) {
            updateMessageImages(userMessageIdtoUse, chunk.images);
          } else if (chunk.branchId !== undefined) {
            // Incognito and guest endpoints always report branch 1; the local
            // branch ids (set by edit/regenerate) are authoritative there.
            if (!isIncognito && auth.user) {
              updateMessageBranchId(aiMessageIdtoUse, chunk.branchId);
              updateMessageBranchId(userMessageIdtoUse, chunk.branchId);
            }
          } else if (chunk.messagePairId !== undefined) {
            updateMessagePairId(aiMessageIdtoUse, chunk.messagePairId);
            updateMessagePairId(userMessageIdtoUse, chunk.messagePairId);
          } else if (chunk.model !== undefined) {
            updateMessageModel(aiMessageIdtoUse, chunk.model);
          } else if (chunk.remainingChats !== undefined) {
            if(chunk.remainingChats !== null) {
              // Convert RemainingCount to RemainingResourceCount format
              const remainingResourceCount = {
                used: chunk.remainingChats.used,
                remaining: chunk.remainingChats.remaining,
                total: chunk.remainingChats.total,
                cycleEndDate: chunk.remainingChats.cycleEndDate?.toString() || new Date().toISOString()
              };
              auth.reduceUsageData(remainingResourceCount.remaining);
            } else {
              auth.reduceUsageData(null);
            }
          } else if (chunk.awaitingInput !== undefined) {
            // The card arrived as content; the stream ends next.
            reset();
            stop();
          } else if (chunk.modelFailed !== undefined) {
            if(auth.user) toast("Model currently unavailable, defaulted to auto", "error");
          }
        } 
        if (accumulatedResponse) {
          const resourceStartTags = (accumulatedResponse.match(/<resource>|<artifacts>/g) || []).length;
          const resourceEndTags = (accumulatedResponse.match(/<\/resource>|<\/artifacts>/g) || []).length;

          if (resourceStartTags === resourceEndTags && resourceStartTags > 0) {
            setIsWritingResource(true);
          } else {
            setIsWritingResource(false);
          }
        }
      }
    } finally {
      if (planningOpen) {
        accumulatedResponse += '</planning>';
        appendContent(aiMessageIdtoUse, '</planning>');
      }
      accumulatedResponse = stripTransientStreamMarkup(accumulatedResponse);
      if (bufferRef.current[aiMessageIdtoUse] !== undefined) {
        bufferRef.current[aiMessageIdtoUse] = stripTransientStreamMarkup(
          bufferRef.current[aiMessageIdtoUse]
        );
        scheduleFlush(aiMessageIdtoUse);
      }
      reset();
      stop();
  
      
      // Stop streaming to allow Convex sync
      stopGeneration(aiMessageIdtoUse);
    }

    return accumulatedResponse;
  }, [start, stop, setIsWritingResource, reset, handleCreateResource, toast, currentChatId, isIncognito, updateMessageSearchItems, updateMessageId, updateMessageTextAttachments, updateMessageImages, updateMessageBranchId, updateMessagePairId, updateMessageModel, auth, startStreaming, increaseImageGenerationCount, stopGeneration, startThinking, appendContent, scheduleFlush]);

  return {
    currentThinkingTime,
    processStream,
    reset,
    start,
    stop,
    startThinking
  };
};
